/**
 * IntentClassifier - Smart Routing
 *
 * Classifies user messages into complexity levels and routes them
 * to the appropriate handler: local parser, Ollama, or Claude.
 * Detects magnitude modifiers, multi-intent messages, and undo requests.
 */

import { RichScreenContext } from './ScreenAnalyzer';

export type ActionType =
  | 'resize'
  | 'move'
  | 'recolor'
  | 'refont'
  | 'align'
  | 'create'
  | 'delete'
  | 'reorder'
  | 'restyle'
  | 'theme_change';

export type Magnitude = 'small' | 'medium' | 'large';
export type Complexity = 'trivial' | 'simple' | 'moderate' | 'complex';
export type RouteTo = 'local' | 'ollama' | 'claude';

export interface SubIntent {
  action: ActionType;
  targetHint: string;
  magnitude?: Magnitude;
  value?: any;
  rawText?: string;
}

export interface ClassifiedIntent {
  mode: 'presentation' | 'content';
  complexity: Complexity;
  routeTo: RouteTo;
  subIntents: SubIntent[];
  isUndoRequest: boolean;
  isDesignIntent: boolean;
  isUnsupported: boolean;
  unsupportedReason?: string;
  isThemeChangeRequest: boolean;
  themeChangeHint?: string;
  confidence: number;
}

// Undo patterns
const UNDO_PATTERNS = [
  /^undo$/i,
  /\bundo\s+(that|last|it|the last)\b/i,
  /\brevert\b/i,
  /\bput\s+it\s+back\b/i,
  /\bgo\s+back\b/i,
  /\bundo\b/i,
];

// Design intent patterns (complex, needs Claude)
const DESIGN_PATTERNS = [
  /\b(make|look)\s+(it\s+)?(more\s+)?(professional|modern|clean|elegant|polished|minimal|beautiful)/i,
  /\bfix\s+(the\s+)?(design|layout|contrast|readability|spacing|alignment)/i,
  /\bimprove\s+(the\s+)?(design|look|appearance|style|visual|layout)/i,
  /\bredesign\b/i,
];

// Unsupported operation patterns - things users ask for that we can't do yet
const UNSUPPORTED_PATTERNS: Array<{ pattern: RegExp; reason: string }> = [
  // Text content changes - would require full recompile which breaks images
  { pattern: /\b(expand|elaborate|simplify|rewrite|rephrase)\s+(the\s+)?(text|content|copy|paragraph|bullet)/i, reason: "I can't edit text content yet. Double-click the text to edit it directly." },
  { pattern: /\b(add|write|insert)\s+(more\s+)?(text|content|detail|information|words)\b/i, reason: "I can't add text content yet. Double-click the text to edit it directly." },
  { pattern: /\b(change|edit|update)\s+(the\s+)?(text|content|wording).*(to|say|read)\b/i, reason: "I can't edit text content yet. Double-click the text to edit it directly." },
  { pattern: /\b(duplicate|clone|copy)\s+(this\s+)?(screen|slide|page)\b/i, reason: "I can't duplicate screens yet. You can do this from the screen navigator on the left." },
  { pattern: /\b(add|insert|create)\s+(a\s+)?(new\s+)?(screen|slide|page)\b/i, reason: "I can't add new screens yet. Use the + button in the screen navigator." },
  { pattern: /\b(delete|remove)\s+(this\s+)?(screen|slide|page)\b/i, reason: "I can't delete screens yet. Right-click the screen in the navigator to delete it." },
  { pattern: /\b(reorder|rearrange|move)\s+(the\s+)?(screens?|slides?|pages?)\b/i, reason: "I can't reorder screens yet. Drag screens in the navigator to reorder them." },
  { pattern: /\bcopy\s+(the\s+)?(title|text|content)\s+(to|from|over)\b/i, reason: "I can't copy content between elements or screens yet. Try selecting and copying manually." },
  { pattern: /\b(animate|animation|transition|fade|slide\s+in|fly\s+in)\b/i, reason: "I can't add animations or transitions yet." },
  { pattern: /\b(export|download|save\s+as|pdf)\b/i, reason: "I can't export or download. Use the Share menu to export your flow." },
  { pattern: /\b(link|hyperlink|navigate)\s+(to|between)\b/i, reason: "I can't add links or navigation between screens yet." },
  { pattern: /\b(upload|import)\s+(an?\s+)?(image|photo|picture|video|file)\b/i, reason: "I can't upload media yet. Use the toolbar to add images and videos." },
  { pattern: /\b(generate|create|make|draw|design)\s+(an?\s+|me\s+)?(image|picture|photo|illustration|graphic|icon|logo|artwork|banner)\b/i, reason: "AI image generation is coming soon! For now, use the toolbar to add images." },
  { pattern: /\b(add|insert|put|place)\s+(an?\s+)?(image|picture|photo|illustration|graphic|icon|logo)\s+(of|about|showing|with|for)\b/i, reason: "AI image generation is coming soon! For now, use the toolbar to add images." },
  { pattern: /\b(generate|create|make)\s+(an?\s+|me\s+)?(video|animation|clip|movie|reel)\b/i, reason: "AI video generation is coming soon!" },
  { pattern: /\b(add|insert|put|place)\s+(an?\s+)?(video|clip|movie|reel)\s+(of|about|showing|with|for)\b/i, reason: "AI video generation is coming soon!" },
  { pattern: /\b(crop|trim|mask)\s+(the\s+)?(image|photo|picture|video)\b/i, reason: "I can't crop or mask media yet." },
  { pattern: /\b(group|ungroup)\b/i, reason: "I can't group or ungroup elements yet." },
  { pattern: /\b(lock|unlock)\s+(the\s+)?(element|object|text|image|shape)\b/i, reason: "I can't lock or unlock elements yet." },
];

// Theme change patterns
const THEME_CHANGE_PATTERNS: Array<{ pattern: RegExp; hintGroup: number }> = [
  { pattern: /\b(?:change|switch|set|use|apply)\s+(?:the\s+)?theme\s+(?:to\s+)?(\w[\w-]*)/i, hintGroup: 1 },
  { pattern: /\b(?:make\s+it|switch\s+to|go)\s+(?:more\s+)?(dark|light|darker|lighter|minimal|professional|corporate|creative|colorful|neon|modern|elegant)/i, hintGroup: 1 },
  { pattern: /\buse\s+(?:a\s+)?([\w-]+)\s+(?:theme|look|style)\b/i, hintGroup: 1 },
  { pattern: /\b(?:make\s+it|go)\s+(darker|lighter)\b/i, hintGroup: 1 },
  { pattern: /\b(darker|lighter)\s+(?:theme|look|design)\b/i, hintGroup: 1 },
  { pattern: /\b(?:change|switch)\s+(?:to\s+)?(?:a\s+)?([\w-]+)\s+theme\b/i, hintGroup: 1 },
];

// Magnitude modifiers
const SMALL_MODIFIERS = /\b(a\s+bit|slightly|a\s+little|a\s+touch|marginally|somewhat|tiny\s+bit)\b/i;
const LARGE_MODIFIERS = /\b(much|way|a\s+lot|significantly|dramatically|extremely|really|very|super|hugely|massively)\b/i;
const DOUBLE_MODIFIER = /\bdouble\b/i;
const HALF_MODIFIER = /\bhalf\b/i;

// Resize direction detection
const RESIZE_UP = /\b(bigger|larger|increase\s+size|grow|expand|scale\s+up|enlarge)\b/i;
const RESIZE_DOWN = /\b(smaller|decrease\s+size|shrink|scale\s+down|reduce)\b/i;

// Action patterns
const ACTION_PATTERNS: Array<{ pattern: RegExp; action: ActionType; meta?: string }> = [
  // Resize
  { pattern: RESIZE_UP, action: 'resize', meta: 'up' },
  { pattern: RESIZE_DOWN, action: 'resize', meta: 'down' },
  { pattern: /\b(font\s+size|text\s+size)\b/i, action: 'resize' },
  { pattern: /\b(set|change)\s+.+?\s+to\s+\d+\s*(?:pt|px|points?|pixels?)\b/i, action: 'resize' },
  { pattern: /\b(double|half)\s+(the\s+)?(size|font|title|subtitle|body|text|heading)\b/i, action: 'resize' },

  // Move
  { pattern: /\b(move|shift|position|place|put)\b/i, action: 'move' },
  { pattern: /\b(nudge)\b/i, action: 'move' },

  // Recolor
  { pattern: /\b(color|colour|background)\b/i, action: 'recolor' },
  { pattern: /\b(blue|red|green|yellow|orange|purple|pink|white|black|gray|grey|navy|teal|dark|light)\b/i, action: 'recolor' },

  // Refont
  { pattern: /\b(font|typeface|helvetica|arial|times|georgia|sans|serif|monospace)\b/i, action: 'refont' },
  { pattern: /\b(bold|italic|bolder|lighter)\b/i, action: 'refont' },

  // Align
  { pattern: /\b(center|centre|align|left-align|right-align|middle)\b/i, action: 'align' },

  // Create
  { pattern: /\b(add|create|insert)\s+(a\s+)?(new\s+)?(shape|rectangle|circle|box|text|element)\b/i, action: 'create' },

  // Delete
  { pattern: /\b(delete|remove|get\s+rid\s+of)\b/i, action: 'delete' },

  // Reorder
  { pattern: /\b(bring|send)\s+(to\s+)?(front|back|forward|backward)\b/i, action: 'reorder' },
  { pattern: /\b(on\s+top|behind|in\s+front)\b/i, action: 'reorder' },
];

// Target hint patterns
const TARGET_PATTERNS: Array<{ pattern: RegExp; hint: string }> = [
  { pattern: /\btitle\b/i, hint: 'title' },
  { pattern: /\bsubtitle\b/i, hint: 'subtitle' },
  { pattern: /\bbody\b/i, hint: 'body' },
  { pattern: /\bheading\b/i, hint: 'title' },
  { pattern: /\bimage\b/i, hint: 'image' },
  { pattern: /\bpicture\b/i, hint: 'image' },
  { pattern: /\bphoto\b/i, hint: 'image' },
  { pattern: /\bvideo\b/i, hint: 'video' },
  { pattern: /\bshape\b/i, hint: 'shape' },
  { pattern: /\brectangle\b/i, hint: 'shape' },
  { pattern: /\bbox\b/i, hint: 'shape' },
  { pattern: /\bcircle\b/i, hint: 'shape' },
  { pattern: /\b(all\s+text|all\s+the\s+text)\b/i, hint: 'all text' },
  { pattern: /\b(all\s+images|all\s+the\s+images)\b/i, hint: 'all images' },
  { pattern: /\beverything\b/i, hint: 'everything' },
  { pattern: /\ball\b/i, hint: 'all' },
  { pattern: /\bbackground\b/i, hint: 'background' },
  { pattern: /\bthe\s+text\b/i, hint: 'text' },
  { pattern: /\bit\b/i, hint: 'it' },
];

// Explicit value patterns
const EXPLICIT_SIZE_PATTERN = /\b(\d+)\s*(?:pt|px|points?|pixels?)\b/i;
const EXPLICIT_POSITION_PATTERN = /\b(?:to|at)\s+(\d+)\s*[,x]\s*(\d+)\b/i;

/**
 * Detect magnitude from message
 */
function detectMagnitude(msg: string): Magnitude {
  if (SMALL_MODIFIERS.test(msg)) return 'small';
  if (LARGE_MODIFIERS.test(msg)) return 'large';
  return 'medium';
}

/**
 * Extract explicit value if present
 */
function extractExplicitValue(msg: string, action: ActionType): any | undefined {
  if (action === 'resize') {
    const match = msg.match(EXPLICIT_SIZE_PATTERN);
    if (match) return parseInt(match[1], 10);
  }

  if (DOUBLE_MODIFIER.test(msg)) return { multiplier: 2 };
  if (HALF_MODIFIER.test(msg)) return { multiplier: 0.5 };

  return undefined;
}

/**
 * Extract target hint from message
 */
function extractTargetHint(msg: string): string {
  for (const { pattern, hint } of TARGET_PATTERNS) {
    if (pattern.test(msg)) return hint;
  }
  return 'title'; // default
}

/**
 * Decompose a message into sub-intents
 */
function decompose(msg: string): SubIntent[] {
  const intents: SubIntent[] = [];
  const magnitude = detectMagnitude(msg);

  // Split on "and" to handle multi-intent
  const parts = msg.split(/\s+and\s+/i);

  for (const part of parts) {
    const matched = new Set<ActionType>();

    for (const { pattern, action, meta } of ACTION_PATTERNS) {
      if (pattern.test(part) && !matched.has(action)) {
        matched.add(action);
        const targetHint = extractTargetHint(part);
        let value = extractExplicitValue(part, action);

        // For resize, encode direction if no explicit value
        if (action === 'resize' && value === undefined && meta) {
          value = { direction: meta };
        }

        intents.push({
          action,
          targetHint,
          magnitude,
          value,
          rawText: part,
        });
      }
    }

    // If no action matched but we have a color word, treat as recolor
    if (matched.size === 0) {
      const colorMatch = part.match(
        /\b(blue|red|green|yellow|orange|purple|pink|white|black|gray|grey|navy|teal)\b/i
      );
      if (colorMatch) {
        intents.push({
          action: 'recolor',
          targetHint: extractTargetHint(part),
          magnitude,
          value: colorMatch[1].toLowerCase(),
          rawText: part,
        });
      }
    }
  }

  // If nothing matched, create a generic intent
  if (intents.length === 0) {
    intents.push({
      action: 'restyle',
      targetHint: extractTargetHint(msg),
      magnitude,
    });
  }

  return intents;
}

/**
 * Determine if multiple elements are being targeted
 */
function isMultiTarget(msg: string): boolean {
  return /\b(all|every|both|each|everything)\b/i.test(msg);
}

/**
 * Classify a user message into an intent with routing
 */
export function classifyIntent(
  message: string,
  context?: RichScreenContext
): ClassifiedIntent {
  const msg = message.toLowerCase().trim();

  // Check for undo
  for (const pattern of UNDO_PATTERNS) {
    if (pattern.test(msg)) {
      return {
        mode: 'presentation',
        complexity: 'trivial',
        routeTo: 'local',
        subIntents: [],
        isUndoRequest: true,
        isDesignIntent: false,
        isUnsupported: false,
        isThemeChangeRequest: false,
        confidence: 0.99,
      };
    }
  }

  // Check for unsupported operations
  for (const { pattern, reason } of UNSUPPORTED_PATTERNS) {
    if (pattern.test(msg)) {
      return {
        mode: 'presentation',
        complexity: 'trivial',
        routeTo: 'local',
        subIntents: [],
        isUndoRequest: false,
        isDesignIntent: false,
        isUnsupported: true,
        unsupportedReason: reason,
        isThemeChangeRequest: false,
        confidence: 0.95,
      };
    }
  }

  // Check for theme change requests
  for (const { pattern, hintGroup } of THEME_CHANGE_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      return {
        mode: 'presentation',
        complexity: 'trivial',
        routeTo: 'local',
        subIntents: [],
        isUndoRequest: false,
        isDesignIntent: false,
        isUnsupported: false,
        isThemeChangeRequest: true,
        themeChangeHint: match[hintGroup]?.toLowerCase(),
        confidence: 0.95,
      };
    }
  }

  // Check for design intent (complex)
  for (const pattern of DESIGN_PATTERNS) {
    if (pattern.test(msg)) {
      return {
        mode: 'presentation',
        complexity: 'complex',
        routeTo: 'claude',
        subIntents: decompose(msg),
        isUndoRequest: false,
        isDesignIntent: true,
        isUnsupported: false,
        isThemeChangeRequest: false,
        confidence: 0.85,
      };
    }
  }

  // Decompose into sub-intents
  const subIntents = decompose(msg);
  const multiTarget = isMultiTarget(msg);

  // Determine complexity
  let complexity: Complexity;
  let routeTo: RouteTo;
  let confidence: number;

  if (subIntents.length === 1 && !multiTarget) {
    const intent = subIntents[0];
    const hasExplicitValue = intent.value !== undefined;
    const hasExplicitTarget = !['it', 'title'].includes(intent.targetHint) ||
      msg.includes('title');

    if (hasExplicitValue || (hasExplicitTarget && ['recolor', 'align', 'delete', 'reorder'].includes(intent.action))) {
      complexity = 'trivial';
      routeTo = 'local';
      confidence = 0.9;
    } else if (intent.action === 'resize' || intent.action === 'move') {
      // Size/move without explicit value — can be local with magnitude
      complexity = 'trivial';
      routeTo = 'local';
      confidence = 0.85;
    } else {
      complexity = 'simple';
      routeTo = 'ollama';
      confidence = 0.75;
    }
  } else if (subIntents.length <= 3 || multiTarget) {
    complexity = 'moderate';
    routeTo = 'ollama';
    confidence = 0.7;
  } else {
    complexity = 'complex';
    routeTo = 'claude';
    confidence = 0.6;
  }

  // Content mode detection
  const contentPatterns = [
    /\b(change|edit|update|rewrite)\s+(the\s+)?(text|words|content)\s+(to|say|read)\b/i,
    /\b(add|insert)\s+(a\s+)?(slide|screen|section|bullet)\b/i,
    /\b(write|generate|create)\s+(new\s+)?(content|text|copy)\b/i,
  ];

  const isContentMode = contentPatterns.some((p) => p.test(msg));

  return {
    mode: isContentMode ? 'content' : 'presentation',
    complexity,
    routeTo,
    subIntents,
    isUndoRequest: false,
    isDesignIntent: false,
    isUnsupported: false,
    isThemeChangeRequest: false,
    confidence,
  };
}

/**
 * Get magnitude deltas for font size and position changes
 */
export function getMagnitudeDeltas(magnitude: Magnitude): {
  fontSize: number;
  position: number;
} {
  switch (magnitude) {
    case 'small':
      return { fontSize: 4, position: 20 };
    case 'medium':
      return { fontSize: 10, position: 50 };
    case 'large':
      return { fontSize: 20, position: 100 };
  }
}
